import * as THREE from 'three';
import type { Camera, Intersection, Object3D } from 'three';
import { BuildingSource } from '@visualizer/types';
import { BuildingsComposite } from '@data/geodata/building/BuildingsComposite';
import { SwisstopoBatchedMesh, CustomBuildingsBatchedMesh, ProjectSketchBatchedMesh } from '@data/geodata/building/BuildingsBatchedMesh';
import type { GeoEditManager } from './GeoEditManager';
import type { GeoEditOperation } from '@visualizer/types';
import { TreesBatched } from '../tree/TreesBatched';

type SelectedTarget = { kind: 'building'; source: BuildingSource; id: number } | { kind: 'tree'; id: number };

export class SelectMode {
    private pointer = new THREE.Vector2();
    private raycaster = new THREE.Raycaster();
    private enabled = false;
    private selected = new Map<string, SelectedTarget>();

    // Fields for the raycast logic
    private isPointerDown = false;
    private didDrag = false;
    private downX = 0;
    private downY = 0;
    private readonly dragThresholdPx = 5;

    constructor(
        private camera: Camera,
        private readonly domElement: HTMLElement,
        private readonly buildings: BuildingsComposite | null,
        private readonly trees: TreesBatched | null,
        private readonly editManager: GeoEditManager
    ) {
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
    }

    private makeKey(t: SelectedTarget): string {
        return t.kind === 'building' ? `b:${t.source}:${t.id}` : `t:${t.id}`;
    }

    setCamera(camera: Camera) {
        this.camera = camera;
    }

    enable() {
        if (this.enabled) return;
        this.enabled = true;

        this.domElement.addEventListener('pointerdown', this.handlePointerDown);
        this.domElement.addEventListener('pointermove', this.handlePointerMove);
        this.domElement.addEventListener('pointerup', this.handlePointerUp);
    }

    disable() {
        if (!this.enabled) return;
        this.enabled = false;

        this.domElement.removeEventListener('pointerdown', this.handlePointerDown);
        this.domElement.removeEventListener('pointermove', this.handlePointerMove);
        this.domElement.removeEventListener('pointerup', this.handlePointerUp);

        this.clearSelection();
    }

    clearSelection() {
        if (this.selected.size === 0) return;

        console.debug('[HoverMode] clear highlight');
        this.selected.clear();
        this.buildings?.clearHighlight();
        this.trees?.clearHighlight();
    }

    getSelectedBuildings(): Array<{ source: BuildingSource; id: number }> {
        const list: Array<{ source: BuildingSource; id: number }> = [];

        for (const t of this.selected.values()) {
            if (t.kind === 'building') {
                list.push({ source: t.source, id: t.id });
            }
        }

        return list;
    }

    setErase(enabled: boolean) {
        if (!enabled) return;

        if (this.selected.size === 0) {
            console.debug('[HoverMode] erase requested but nothing selected');
            return;
        }

        this.deleteSelected();
    }

    private deleteSelected() {
        this.buildings?.clearHighlight();
        this.trees?.clearHighlight?.();

        for (const t of this.selected.values()) {
            const op: GeoEditOperation =
                t.kind === 'building'
                    ? ({ command: 'REMOVE', dataType: 'building', id: t.id, source: t.source } as GeoEditOperation)
                    : ({ command: 'REMOVE', dataType: 'tree', id: t.id } as GeoEditOperation);

            this.editManager.apply(op);
        }

        this.selected.clear();
    }

    // ---------------------------------------------------------------------
    // Event handlers
    // ---------------------------------------------------------------------
    private handlePointerDown(event: PointerEvent) {
        if (!this.enabled) return;
        if (event.button !== 0) return;

        this.isPointerDown = true;
        this.didDrag = false;
        this.downX = event.clientX;
        this.downY = event.clientY;
    }

    private handlePointerMove(event: PointerEvent) {
        if (!this.enabled) return;

        // keep pointer coords updated for raycaster
        const rect = this.domElement.getBoundingClientRect();
        this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // if LMB is held, detect drag
        if (this.isPointerDown && (event.buttons & 1) === 1) {
            const dx = event.clientX - this.downX;
            const dy = event.clientY - this.downY;
            if (dx * dx + dy * dy > this.dragThresholdPx * this.dragThresholdPx) {
                this.didDrag = true;
            }
        }
    }

    private handlePointerUp(event: PointerEvent) {
        if (!this.enabled) return;
        if (event.button !== 0) return;

        this.isPointerDown = false;

        // if user dragged camera, do NOT select
        if (this.didDrag) return;

        // treat as click (raycast now)
        const rect = this.domElement.getBoundingClientRect();
        this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const hovered = this.updateHover();
        if (!hovered) return;

        this.toggleSelection(hovered);
    }

    private toggleSelection(t: SelectedTarget) {
        const key = this.makeKey(t);

        if (this.selected.has(key)) this.selected.delete(key);
        else this.selected.set(key, t);

        this.updateHighlightFromSelection();
    }

    private updateHighlightFromSelection() {
        this.buildings?.clearHighlight();
        this.trees?.clearHighlight?.();

        for (const t of this.selected.values()) {
            if (t.kind === 'building') {
                this.buildings?.highlightBuilding(t.source, t.id);
            } else {
                this.trees?.highlightObject?.(t.id);
            }
        }
    }

    // ---------------------------------------------------------------------
    // Core select logic
    // ---------------------------------------------------------------------
    private updateHover(): SelectedTarget | null {
        this.raycaster.setFromCamera(this.pointer, this.camera);

        const targets = this.collectObjectsRaycastTargets();
        const intersections = this.raycaster.intersectObjects(targets, true);

        if (intersections.length === 0) {
            console.debug('[SelectMode] no intersections');
            return null;
        }

        for (const hit of intersections) {
            // Trees first
            if (this.trees && this.isUnder(hit.object, this.trees)) {
                const id = this.trees.getTreeIdFromIntersection(hit);
                if (id != null) return { kind: 'tree', id };
                continue;
            }

            // Buildings
            const b = this.resolveBuildingFromIntersection(hit);
            if (b) return { kind: 'building', ...b };
        }

        return null;
    }

    private isUnder(obj: Object3D, root: Object3D): boolean {
        let c: Object3D | null = obj;
        while (c) {
            if (c === root) return true;
            c = c.parent;
        }
        return false;
    }

    // ---------------------------------------------------------------------
    // Helpers: from intersection → (source, buildingId)
    // ---------------------------------------------------------------------
    private collectObjectsRaycastTargets(): Object3D[] {
        const targets: Object3D[] = [];

        if (this.buildings) {
            this.buildings.buildings.forEach((geoObject) => {
                geoObject.traverse((child) => {
                    if (child instanceof THREE.BatchedMesh) {
                        targets.push(child);
                    }
                });
            });
        }

        if (this.trees) {
            this.trees.traverse((child) => {
                if (child instanceof THREE.BatchedMesh && (child as any).userData?.treeGeometry === true) {
                    targets.push(child);
                }
            });
        }

        if (targets.length === 0) {
            console.debug('[SelectMode] no batched mesh targets found for raycasting');
        }

        console.debug(
            '[SelectMode] raycast targets',
            targets.length,
            targets.map((t) => t.constructor.name)
        );

        return targets;
    }

    private resolveBuildingFromIntersection(hit: Intersection): { source: BuildingSource; id: number } | null {
        // climb up to the GeoObjectBatched (Swisstopo/Custom/Sketch)
        const batchedRoot = this.findBatchedRoot(hit.object);
        if (!batchedRoot) return null;

        const source: BuildingSource | undefined = batchedRoot.userData.buildingSource;
        if (source == null) return null;

        let id: number | null = null;

        if (batchedRoot instanceof SwisstopoBatchedMesh) {
            id = batchedRoot.getBuildingIdFromIntersection(hit);
        } else if (batchedRoot instanceof CustomBuildingsBatchedMesh) {
            id = batchedRoot.getBuildingIdFromIntersection(hit);
        } else if (batchedRoot instanceof ProjectSketchBatchedMesh) {
            id = batchedRoot.getBuildingIdFromIntersection(hit);
        }

        if (id == null) {
            console.debug('[SelectMode] could not resolve building id from intersection', {
                object: hit.object.name,
                instanceId: hit.instanceId,
                batchedRoot: batchedRoot,
                source: source,
            });
            return null;
        }

        return { source, id };
    }

    private findBatchedRoot(obj: Object3D): SwisstopoBatchedMesh | CustomBuildingsBatchedMesh | ProjectSketchBatchedMesh | null {
        let current: Object3D | null = obj;
        while (current) {
            if (
                current instanceof SwisstopoBatchedMesh ||
                current instanceof CustomBuildingsBatchedMesh ||
                current instanceof ProjectSketchBatchedMesh
            ) {
                return current;
            }
            current = current.parent;
        }
        return null;
    }
}
